import { Link } from "react-router-dom";
import PageIntro from "@/components/base/PageIntro";
import usePageMeta from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-newspaper-line",
    title: "资讯要清楚",
    description: "不只告诉你发生了什么，也尽量交代清楚前因后果，让每一条消息都有上下文。",
  },
  {
    icon: "ri-film-line",
    title: "推荐要负责",
    description: "推荐不是热度搬运。我们更愿意推荐那些看完之后还会想起的作品。",
  },
  {
    icon: "ri-chat-smile-3-line",
    title: "讨论要友善",
    description: "观点可以不同，表达需要体面。我们珍惜每一个愿意认真聊内容的人。",
  },
];

const team = [
  { name: "陆青禾", role: "主编", focus: "电影报道 / 产业观察", initial: "陆" },
  { name: "周映", role: "剧集编辑", focus: "剧集资讯 / 片单策划", initial: "周" },
  { name: "许之南", role: "人物作者", focus: "人物专访 / 深度内容", initial: "许" },
  { name: "林一鸣", role: "综艺编辑", focus: "综艺动态 / 现场报道", initial: "林" },
];

const sections = [
  {
    title: "免责声明",
    items: [
      "本站为产品演示项目，页面中的影视作品、综艺节目、人物、账号与评论均为虚构示例，不对应任何真实作品或事件。",
      "站内评分、推荐指数、浏览量、点赞与回复数均为演示数据，不代表真实市场表现或用户评价。",
      "收藏、点赞等互动仅保存在本机浏览器，不会上传至服务器，也不构成任何形式的用户数据收集。",
      "如本站内容涉及第三方权益，请联系 hello@hongtao.example，我们会在核实后及时处理。",
    ],
  },
  {
    title: "隐私政策",
    items: [
      "本站不要求注册，也不收集姓名、手机号、身份证件等个人身份信息。",
      "为展示收藏与点赞效果，页面会在本机浏览器中保存少量状态数据，你可以随时通过清除浏览器数据删除。",
      "本站未接入第三方统计与广告服务，演示环境不进行用户行为追踪。",
      "若未来接入账号系统或数据分析服务，我们会在页面上明确说明并更新本政策。",
    ],
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 红桃网",
    "了解红桃网：一个关注电影、电视剧与综艺的娱乐资讯与互动平台。查看我们的内容理念、团队介绍、联系方式与免责声明。",
    "关于红桃网,红桃网团队,娱乐资讯平台,联系方式,免责声明",
  );

  return (
    <>
      <PageIntro
        kicker="关于我们"
        title="关于红桃网"
        description="一个认真聊娱乐的地方。我们相信，好的内容值得被讲清楚，好的讨论值得被认真对待。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
        extra={
          <Link
            to="/"
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        }
      />

      <div className="mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-10">
        <section className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
          <div>
            <p className="font-label text-[11px] tracking-[0.24em] text-primary-600">我们的故事</p>
            <h2 className="mt-2 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[30px]">
              把娱乐资讯做成一件
              <br className="hidden sm:block" />
              值得慢慢看的事
            </h2>
            <p className="mt-4 text-sm leading-[1.95] text-foreground-700">
              红桃网起步于一个小小的观影群。那时我们每周都会在群里争论同一件事：这部片子到底好不好看，为什么好看。
              后来争论变成了文档，文档变成了栏目，栏目变成了你现在看到的这个网站。
            </p>
            <p className="mt-3 text-sm leading-[1.95] text-foreground-600">
              我们想做的其实很简单——把电影、电视剧和综艺里那些值得被讨论的部分整理出来，
              让没看过的人知道值不值得看，让看过的人有个地方把话说完。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: "2019", label: "开始做内容" },
                { value: "3", label: "核心频道" },
                { value: "每天", label: "更新频率" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-3 py-3">
                  <p className="font-label text-lg font-extrabold text-foreground-950">{item.value}</p>
                  <p className="mt-0.5 text-[11px] text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-200">
            <div className="aspect-[4/3] w-full bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20editorial%20workspace%20with%20wooden%20desk%20and%20stacked%20film%20magazines%2C%20vintage%20typewriter%20and%20coffee%20cup%2C%20soft%20afternoon%20sunlight%20through%20linen%20curtains%2C%20plants%20and%20warm%20neutral%20tones%2C%20cozy%20creative%20studio%20photography&width=1200&height=900&seq=ht-about-01&orientation=landscape"
                alt="红桃网编辑工作室"
                title="红桃网编辑工作室 - 关于我们"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="flex items-center justify-between gap-3 bg-background-100 px-4 py-3 text-xs text-foreground-500">
              <span>编辑工作室 · 示意图</span>
              <span className="font-label text-[10px] tracking-widest text-foreground-400">红桃工作室</span>
            </div>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
            <span className="h-4 w-1 rounded-full bg-primary-500" />
            我们在坚持什么
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {values.map((item, index) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span
                  className={[
                    "flex h-10 w-10 items-center justify-center rounded-md text-xl",
                    index === 1 ? "bg-accent-100 text-accent-800" : "bg-primary-50 text-primary-600",
                  ].join(" ")}
                >
                  <i className={item.icon} />
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
            <span className="h-4 w-1 rounded-full bg-primary-500" />
            团队介绍
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 font-heading text-base font-bold text-primary-600">
                  {member.initial}
                </span>
                <p className="mt-3 font-heading text-sm font-semibold text-foreground-950">{member.name}</p>
                <p className="mt-0.5 text-xs text-primary-600">{member.role}</p>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">{member.focus}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-foreground-400">团队成员与职责均为演示信息。</p>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1.2fr] md:gap-10">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-mail-send-line text-primary-500" />
                联系我们
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                内容合作、纠错建议或是单纯想聊聊最近看的片子，都欢迎写邮件给我们。
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-500" />
                  hello@hongtao.example
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-primary-500" />
                  城市文创园 B 座 7 层
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-500" />
                  工作日 10:00 - 19:00
                </li>
              </ul>
              <div className="mt-4 flex items-center gap-2">
                {[
                  { icon: "ri-weibo-line", label: "微博" },
                  { icon: "ri-wechat-line", label: "微信" },
                  { icon: "ri-video-line", label: "短视频" },
                ].map((item) => (
                  <span
                    key={item.label}
                    title={`${item.label}（演示链接）`}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600"
                  >
                    <i className={`${item.icon} text-base`} />
                  </span>
                ))}
                <span className="text-[11px] text-foreground-400">社交账号为演示占位</span>
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-4">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
                <i className="ri-information-line text-primary-500" />
                关于本站的说明
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                红桃网目前为演示站点，未接入登录、支付、数据库与视频播放能力。
                所有资讯、评分、帖子与用户信息均为虚构示例，仅用于展示产品结构与交互效果。
                如果你在浏览中发现任何不合适的内容，欢迎通过上面的邮箱与我们联系。
              </p>
            </div>
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {sections.map((section) => (
            <div key={section.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-file-shield-2-line text-primary-500" />
                {section.title}
              </h2>
              <ul className="mt-3 space-y-2.5">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-2 text-xs leading-relaxed text-foreground-600">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 border-t border-background-200 pt-8 text-center">
          <p className="text-sm text-foreground-500">谢谢你读到这里。</p>
          <Link
            to="/"
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            回到首页
          </Link>
        </div>
      </div>
    </>
  );
}